import { useEffect, useRef, useCallback } from 'react';
import { useNTContext } from '../NTContext';
import { useNTSelectionContext } from '../NTSelectionContext';
import { useNTUtilsContext } from '../NTUtilsContext';
import { useRightPanel } from './RightPanelContext';
import { registerModule, unregisterModule } from './moduleRegistry';
import { RightPanelModule } from './RightPanelContext';
import {
  chartModuleConfig,
  commentsModuleConfig,
  transactionsModuleConfig,
  aiAnalysisModuleConfig,
  reportQueueModuleConfig,
} from '../../../components/RightPanelModules';
import { useReportsStore } from '../../../../hooks/useReportsStore';
import { getQueueCells } from '@hone-automation/common';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { AskRequest } from '@hone-automation/common';
import { ReportViewModel, ReportColumn } from 'types/report';
import { HoneReportTypes } from '../../../../constants';

/**
 * Custom hook to handle module registration for the right panel
 *
 * This hook encapsulates the logic for registering all modules with their
 * data transformation functions and props
 *
 * @param aiData - Separate AI data object that doesn't change with transformedData updates
 */
export const useModuleRegistration = (aiData?: any, rawData?: ReportViewModel) => {
  const { enableQueueReport } = useFlags();

  // Keep a ref to the latest rawData so callbacks always see current data
  const rawDataRef = useRef(rawData);
  rawDataRef.current = rawData;

  // ----- Helper conversion functions -----
  const chartToTable = (idx: number | string | null): number | string | null => {
    if (idx === null || idx === undefined) return null;
    const n = typeof idx === 'number' ? idx : parseInt(idx as string, 10);
    return isNaN(n) ? null : n + 1; // chart 0-based → table 1-based
  };

  const tableToChart = (idx: number | string | null): number | string | null => {
    if (idx === null || idx === undefined) return null;
    const n = typeof idx === 'number' ? idx : parseInt(idx as string, 10);
    return isNaN(n) ? null : Math.max(n - 1, 0); // table 1-based → chart 0-based
  };

  // Get contexts
  const ntContext = useNTContext();
  const ntSelectionContext = useNTSelectionContext();
  const { actions, setActions } = useNTUtilsContext();
  const { activeModuleId, setActiveModuleId, isPanelVisible, setPanelVisible } = useRightPanel();
  const { aiReport, setAiReport, reportQueue, setReportQueue } = useReportsStore(state => ({
    aiReport: state.aiReport,
    setAiReport: state.setAiReport,
    reportQueue: state.reportQueue,
    setReportQueue: state.setReportQueue,
  }));

  // Sync actions.detailsPanel with panel visibility
  useEffect(() => {
    if (actions.detailsPanel !== isPanelVisible) {
      setPanelVisible(actions.detailsPanel);
    }
  }, [actions.detailsPanel, isPanelVisible, setPanelVisible]);

  // Callback for report queue: scroll/highlight the matching cell when a request is selected
  const onRequestSelect = useCallback(
    (request: AskRequest | null) => {
      if (!request?.identifier) return;
      const data = rawDataRef.current;
      if (!data?.columns?.[0]?.data) return;

      const rows = data.columns[0].data as Array<{ id: string; refTitle?: string }>;
      const reportType: string | undefined = data.type;
      const isBSorCF = reportType === HoneReportTypes.BalanceSheet || reportType === HoneReportTypes.CashFlow;

      // Find the matching row by identifier
      const matchedRow = rows.find((row: { id: string; refTitle?: string }) => {
        if (!row.refTitle) return false;
        if (row.refTitle === request.identifier) return true;
        // BS/CF rows have a prefix (e.g. "Assets Cash") — strip it for matching
        if (isBSorCF) {
          const stripped = row.refTitle.split(' ').slice(1).join(' ');
          return stripped === request.identifier;
        }
        return false;
      });

      if (matchedRow) {
        ntSelectionContext.handleSelect(matchedRow.id, true);
      }

      // Find and select the matching column (skip for AP Aging)
      const period =
        request.period || (request.transaction?.date ? String(request.transaction.date).split('T')[0] : null);
      if (period && reportType !== HoneReportTypes.ApAging) {
        const columns = data.columns as Array<Pick<ReportColumn, 'date'>>;
        const colIndex = columns.findIndex(
          (col: Pick<ReportColumn, 'date'>) => col.date?.start === period || col.date?.end === period
        );
        if (colIndex >= 1) {
          ntSelectionContext.handleColumnSelect(colIndex, true);
        }
      }
    },
    [ntSelectionContext]
  );

  // Register all modules
  useEffect(() => {
    // Register chart module with data transformation
    registerModule({
      ...chartModuleConfig,
      props: {
        onClose: () => {
          setPanelVisible(false);
          setActiveModuleId(null);
          setActions({ ...actions, detailsPanel: false });
        },
        onSelectionChange: (rowId: string) => ntSelectionContext.handleSelect(rowId, true),
        // Handle column selection changes from the chart
        onColumnSelectionChange: (columnIndex: number | string | null) => {
          // Ensure we're passing the correct type to handleColumnSelect
          // Chart gives 0-based index, table expects 1-based → shift by +1
          //console.log('columnIndex from useModuleRegistration', columnIndex);
          ntSelectionContext.handleColumnSelect(chartToTable(columnIndex), true);
        },
        // Convert string column IDs to numbers when needed for the chart
        // selectedColumn will be provided dynamically in getModuleProps
      },
      transformData: rawData => {
        // Transform data for chart module with all necessary context data
        // selection is now expected to be an object: { rowId, columnId }
        return {
          chartData: rawData,
          initialFilters: ntContext.filters
            ? {
                actuals: ntContext.filters.actuals,
                budget: ntContext.filters.budget,
                variance: ntContext.filters.variance,
                money: ntContext.filters.money,
                percentage: ntContext.filters.percentage,
              }
            : undefined,
          // Backend handles reverse via URL parameter - data already in correct order
          reversed: false,
          selectedRow: ntSelectionContext.cellStates.selected,
          selectedColumn: ntSelectionContext.cellStates.selectedColumn
            ? parseInt(String(ntSelectionContext.cellStates.selectedColumn)) - 1
            : null,
        };
      },
    });

    // Register comments module with data transformation
    registerModule({
      ...commentsModuleConfig,
      transformData: (rawData, selection) => {
        // Transform data for comments module
        return {
          comments: [], // This would be populated from rawData
          selectedRowId: selection,
          selectedColumnId: selection,
        };
      },
    });

    // Register transactions module with data transformation
    registerModule({
      ...transactionsModuleConfig,
      transformData: (rawData, selection) => {
        // Transform data for transactions module
        return {
          transactions: [], // This would be populated from rawData based on selection
          selectedRowId: selection,
          selectedColumnId: selection,
        };
      },
    });

    // Register AI analysis module with data transformation
    registerModule({
      ...aiAnalysisModuleConfig,
      notListOnModuleBrowser: aiAnalysisModuleConfig.notListOnModuleBrowser,
      props: {
        onClose: () => {
          setPanelVisible(false);
          setActiveModuleId(null);
          if (aiReport) {
            setAiReport(false);
          } else if (reportQueue) {
            setReportQueue(false);
          } else {
            setActions({ ...actions, detailsPanel: false });
          }
        },
      },
      transformData: (rawData, selection) => {
        // Use separate aiData object if available to avoid re-renders from transformedData changes
        // aiData contains: { reportId, aiAnalysis, type }
        const dataToUse = aiData || rawData;

        // Transform data for AI analysis module
        return {
          reportData: dataToUse,
          selectedItem: selection,
          selectedRowId: selection,
          selectedColumnId: selection,
          // Backend handles reverse via URL parameter - data already in correct order
          reversed: false,
        };
      },
    });

    if (enableQueueReport) {
      // Register Report Queue module with data transformation
      registerModule({
        ...reportQueueModuleConfig,
        notListOnModuleBrowser: reportQueueModuleConfig.notListOnModuleBrowser,
        props: {
          onClose: () => {
            setPanelVisible(false);
            setActiveModuleId(null);
            if (reportQueue) {
              setReportQueue(false);
            } else if (aiReport) {
              setAiReport(false);
            } else {
              setActions({ ...actions, detailsPanel: false });
            }
          },
          onRequestSelect,
        },
        transformData: rawData => {
          const queueRequests = getQueueCells(rawData) || [];
          const reportId = rawData?.reportId || rawData?.columns?.[0]?.reportId || null;
          const reportType: string | undefined = rawData?.type;

          // Extract column date ranges for P&L Comparison filtering
          const columnDates: NonNullable<ReportColumn['date']>[] = (rawData?.columns || [])
            .filter((col: any) => col.date?.start && col.date?.end)
            .map((col: any) => col.date as NonNullable<ReportColumn['date']>);

          return {
            requests: queueRequests,
            reportId,
            reportType,
            columnDates,
          };
        },
      });
    } else {
      unregisterModule(reportQueueModuleConfig.id);
      if (reportQueue) {
        setReportQueue(false);
      }
    }

    // Set a default module if none is selected
    if (!activeModuleId) {
      setActiveModuleId('chart');
    }
  }, [
    activeModuleId,
    setActiveModuleId,
    ntSelectionContext,
    ntContext,
    ntContext.filters,
    setPanelVisible,
    ntSelectionContext.cellStates,
    actions,
    setActions,
    aiData,
    aiReport,
    setAiReport,
    reportQueue,
    setReportQueue,
    enableQueueReport,
    onRequestSelect,
  ]);

  /**
   * Interface for the hook return value
   */
  interface ModuleRegistrationHook {
    getModuleProps: (module: RightPanelModule, rawData: any) => Record<string, any>;
    togglePanel: () => void;
  }

  // Return utility functions for module handling
  return {
    getModuleProps: (module: RightPanelModule, rawData: any) => {
      // Get selection from context
      const selection = ntSelectionContext ? ntSelectionContext.cellStates.selected : null;

      // Transform data if the module has a transformData function
      const data = module.transformData ? module.transformData(rawData, selection) : rawData;

      const dynamicProps: Record<string, any> = {};

      // Dynamically add selectedColumn for chart module to keep it up-to-date
      if (module.id === 'chart') {
        dynamicProps.selectedColumn = tableToChart(ntSelectionContext.cellStates.selectedColumn);
      }

      return {
        data,
        selection,
        // Static props from registration
        ...module.props,
        // Dynamic overrides (e.g., latest selectedColumn)
        ...dynamicProps,
      };
    },
    togglePanel: () => {
      const newVisibility = !isPanelVisible;
      setActions({ ...actions, detailsPanel: newVisibility });
    },
  };
};
